import { Plural, Trans, useLingui } from '@lingui/react/macro';
import { differenceInBusinessDays } from 'date-fns';

import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import Tooltip from '@/design_system/Tooltip';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconCalendar from '@/icons/Calendar.svg';
import { formatDate } from '@/utils/date';
import useViewPort from '@/utils/useViewport';

export const EstimatedDueDate = ({
  estimatedDate,
  variant,
  displayDayMonthOnly,
}: {
  estimatedDate: Date | null;
  variant: 'info' | 'row' | 'card';
  displayDayMonthOnly?: boolean;
}) => {
  const { t } = useLingui();
  const label = t({
    id: 'components.request-estimated-due-date',
    message: 'Estimated completion date',
  });
  const { isMobile } = useViewPort();

  if (!estimatedDate) {
    return null;
  }

  const durationleftDays = differenceInBusinessDays(estimatedDate, new Date());

  return (
    <Stack
      row={isMobile}
      justifyContent={isMobile ? 'space-between' : undefined}
      alignItems={isMobile ? 'center' : 'flex-start'}
    >
      <Paragraph color="secondary" size={isMobile ? '100' : '200'}>
        {label}
      </Paragraph>
      <Tooltip
        content={
          <span>
            <Trans id="components.request-estimated-due-date.tooltip">
              Duration left:{' '}
              <Plural value={durationleftDays} one="# business day" other="# business days" />
            </Trans>
          </span>
        }
      >
        <Button variant="style-less" style={{ justifyContent: 'normal', alignSelf: 'flex-start' }}>
          <Stack row gap="0.25rem" alignItems="center" flexWrap="nowrap">
            <IconCalendar style={{ fontSize: '1rem' }} />
            <Paragraph
              size={variant === 'card' ? '200' : '100'}
              weight={variant === 'info' ? 'medium' : 'regular'}
              aria-label={label}
            >
              {formatDate(
                estimatedDate,
                displayDayMonthOnly ? { day: 'numeric', month: 'short' } : { dateStyle: 'medium' }
              )}
            </Paragraph>
          </Stack>
        </Button>
      </Tooltip>
    </Stack>
  );
};
